// Sticky add to cart (sections/os-sticky-atc.liquid). Shows the bar once the
// main product section (or only its buy button) has scrolled out of view
// above, hides it again over the footer, and keeps its variant in step with
// the main form.
if (!customElements.get('os-sticky-atc')) {
  customElements.define(
    'os-sticky-atc',
    class OsStickyAtc extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.idInput = this.form?.querySelector('input[name="id"]');
        this.bar = this.querySelector('.os-satc__bar');
        this.pastMain = false;
        this.footerInView = false;
        this.editing = false;
        this.hideAtFooter = this.dataset.hideAtFooter === 'true';

        // The main product form's buy button (main-product's Buy buttons or
        // Add to cart block); the installment form next to it has none.
        const button = [...document.querySelectorAll('[id^="ProductSubmitButton-"]')].find(
          (el) => !el.closest('quick-add-modal, os-sticky-atc, sticky-add-to-cart')
        );
        this.mainForm = button?.form;
        const target =
          this.dataset.showAfter === 'button'
            ? button || this.mainForm
            : button?.closest('.shopify-section') || this.mainForm?.closest('.shopify-section');

        if (target) {
          this.mainObserver = new IntersectionObserver(([entry]) => {
            this.pastMain = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.update();
          });
          this.mainObserver.observe(target);
        } else {
          // No main form on the page (e.g. it was removed): always show.
          this.pastMain = true;
          this.update();
        }

        // Any footer group section on screen hides the bar.
        const footer = document.querySelectorAll('.shopify-section-group-footer-group, body > footer');
        if (this.hideAtFooter && footer.length) {
          const inView = new Set();
          this.footerObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => (entry.isIntersecting ? inView.add(entry.target) : inView.delete(entry.target)));
            this.footerInView = inView.size > 0;
            this.update();
          });
          footer.forEach((el) => this.footerObserver.observe(el));
        }

        // Next frame: setHeight() pads the page, which can resize the bar again.
        this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => this.setHeight()));
        this.resizeObserver.observe(this.bar);

        this.onChange = this.onChange.bind(this);
        document.addEventListener('change', this.onChange);

        this.onSelect = (event) => this.setEditing(event, true);
        this.onDeselect = (event) => this.setEditing(event, false);
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:block:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
        document.addEventListener('shopify:block:deselect', this.onDeselect);
      }

      disconnectedCallback() {
        this.mainObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.controller?.abort();
        document.removeEventListener('change', this.onChange);
        document.removeEventListener('shopify:section:select', this.onSelect);
        document.removeEventListener('shopify:block:select', this.onSelect);
        document.removeEventListener('shopify:section:deselect', this.onDeselect);
        document.removeEventListener('shopify:block:deselect', this.onDeselect);
        document.documentElement.removeAttribute('data-os-satc-open');
      }

      // In the theme editor the bar shows while its section or a block is selected.
      setEditing(event, editing) {
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        this.editing = editing;
        this.update();
      }

      update() {
        const visible = (this.pastMain && !this.footerInView) || this.editing;
        this.classList.toggle('is-visible', visible);
        this.toggleAttribute('inert', !visible);
        this.setHeight();
      }

      // Room under the page while the bar shows on this screen size, so it
      // never covers the end of the page. Not needed when it hides at the
      // footer (and the page would jump as it leaves).
      setHeight() {
        const open = !this.hideAtFooter && this.classList.contains('is-visible') && this.getClientRects().length > 0;
        const root = document.documentElement;
        // An attribute: the web font loader rewrites the html element's classes.
        root.toggleAttribute('data-os-satc-open', open);
        if (open) root.style.setProperty('--os-satc-height', `${this.bar.offsetHeight}px`);
      }

      onChange(event) {
        if (!this.mainForm || !this.idInput) return;
        const input = event.target;
        if (input.name !== 'id' || input.form !== this.mainForm) return;
        if (!input.value || input.value === this.idInput.value) return;
        this.idInput.value = input.value;
        this.refresh(input.value);
      }

      // Re-render this section for the variant: product info and button state.
      refresh(variantId) {
        this.controller?.abort();
        this.controller = new AbortController();
        const url = `${window.location.pathname}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(this.dataset.sectionId)}`;

        fetch(url, { signal: this.controller.signal })
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const source = html.querySelector('os-sticky-atc');
            if (!source) return;

            const product = this.querySelector('[data-os-satc-product]');
            const newProduct = source.querySelector('[data-os-satc-product]');
            if (product && newProduct) product.innerHTML = newProduct.innerHTML;

            const button = this.querySelector('.os-satc__button');
            const newButton = source.querySelector('.os-satc__button');
            if (button && newButton) {
              button.toggleAttribute('disabled', newButton.hasAttribute('disabled'));
              if (newButton.dataset.previewImage) button.dataset.previewImage = newButton.dataset.previewImage;
              button.querySelector('span').textContent = newButton.querySelector('span').textContent;
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }
    }
  );
}
